import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEmpresa } from '../contexts/EmpresaContext';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { colaboradoresService } from '../services/colaboradores.service';
import { useQuery } from '@tanstack/react-query';
import {
  HomeIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  CheckBadgeIcon,
  Bars3Icon,
  XMarkIcon,
  ShieldCheckIcon,
  ClipboardDocumentCheckIcon,
  CreditCardIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  DocumentTextIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import { formatEmpresaDisplayName } from '../utils/formatters';

export default function MobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { empresa, isIntegracoes } = useEmpresa();
  const { hasModulo } = useFeatureAccess();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const pathname = location.pathname;

  // Contagem de candidatos pendentes de aprovação
  const { data: pendingCandidatesCount = 0 } = useQuery({
    queryKey: ['candidatos-pendentes-count', empresa?.id],
    queryFn: () => colaboradoresService.countCandidatosPendentes().catch(() => 0),
    enabled: Boolean(empresa?.id),
    staleTime: 60000,
  });

  const isActive = (path: string) => {
    if (path === '/') return pathname === '/';
    return pathname.startsWith(path);
  };

  const handleLogout = () => {
    setIsMenuOpen(false);
    logout();
    navigate('/login');
  };

  const closeMenu = () => setIsMenuOpen(false);

  return (
    <>
      {/* Barra de Navegação Inferior Fixa (Apenas Mobile: block md:hidden) */}
      <nav
        aria-label="Navegação mobile"
        className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-gray-200/90 shadow-[0_-4px_16px_rgba(0,0,0,0.06)] block md:hidden"
      >
        <div className="flex items-center justify-around h-16 px-2 max-w-lg mx-auto pb-[max(env(safe-area-inset-bottom),0.25rem)]">
          {/* 1. Início */}
          <Link
            to="/"
            onClick={closeMenu}
            className={`flex flex-col items-center justify-center flex-1 py-1 transition-all ${
              isActive('/') && !isMenuOpen
                ? 'text-emerald-700 font-semibold'
                : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            <HomeIcon className={`w-5 h-5 ${isActive('/') && !isMenuOpen ? 'stroke-2' : 'stroke-1.5'}`} />
            <span className="text-[10px] mt-1 tracking-tight">Início</span>
          </Link>

          {isIntegracoes ? (
            hasModulo('integracoes') && (
              <Link
                to="/integracoes"
                onClick={closeMenu}
                className={`flex flex-col items-center justify-center flex-1 py-1 transition-all ${
                  isActive('/integracoes') && !isMenuOpen
                    ? 'text-emerald-700 font-semibold'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                <KeyIcon
                  className={`w-5 h-5 ${isActive('/integracoes') && !isMenuOpen ? 'stroke-2' : 'stroke-1.5'}`}
                />
                <span className="text-[10px] mt-1 tracking-tight">Integrações</span>
              </Link>
            )
          ) : (
            <>
              {/* 2. Organização */}
              <Link
                to="/organizacao"
                onClick={closeMenu}
                className={`flex flex-col items-center justify-center flex-1 py-1 transition-all ${
                  isActive('/organizacao') && !isMenuOpen
                    ? 'text-emerald-700 font-semibold'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                <BuildingOffice2Icon
                  className={`w-5 h-5 ${isActive('/organizacao') && !isMenuOpen ? 'stroke-2' : 'stroke-1.5'}`}
                />
                <span className="text-[10px] mt-1 tracking-tight">Organização</span>
              </Link>

              {/* 3. Agendar (Botão Central em Destaque) */}
              <div className="flex flex-col items-center justify-center flex-1">
                <Link
                  to="/agendamento/cadastro"
                  onClick={closeMenu}
                  className="relative -top-3.5 bg-gradient-to-tr from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400 text-white rounded-full p-3.5 shadow-lg shadow-emerald-600/30 transition-all transform active:scale-95 flex items-center justify-center focus:outline-none focus:ring-4 focus:ring-emerald-500/20"
                  title="Novo Agendamento"
                >
                  <CalendarDaysIcon className="w-6 h-6 stroke-2" />
                </Link>
                <span className="text-[10px] -mt-2 font-medium text-emerald-800 tracking-tight">Agendar</span>
              </div>

              {/* 4. Candidatos */}
              <Link
                to="/candidatos-aprovacao"
                onClick={closeMenu}
                className={`relative flex flex-col items-center justify-center flex-1 py-1 transition-all ${
                  isActive('/candidatos-aprovacao') && !isMenuOpen
                    ? 'text-emerald-700 font-semibold'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                <div className="relative">
                  <CheckBadgeIcon
                    className={`w-5 h-5 ${isActive('/candidatos-aprovacao') && !isMenuOpen ? 'stroke-2' : 'stroke-1.5'}`}
                  />
                  {pendingCandidatesCount > 0 && (
                    <span className="absolute -top-1.5 -right-2.5 min-w-[16px] h-4 px-1 bg-amber-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center border-2 border-white shadow-xs">
                      {pendingCandidatesCount > 99 ? '99+' : pendingCandidatesCount}
                    </span>
                  )}
                </div>
                <span className="text-[10px] mt-1 tracking-tight">Candidatos</span>
              </Link>
            </>
          )}

          {/* 5. Menu Mais */}
          <button
            type="button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`flex flex-col items-center justify-center flex-1 py-1 transition-all ${
              isMenuOpen ? 'text-emerald-700 font-semibold' : 'text-gray-500 hover:text-gray-900'
            }`}
            aria-label="Abrir menu de opções adicionais"
          >
            <Bars3Icon className={`w-5 h-5 ${isMenuOpen ? 'stroke-2' : 'stroke-1.5'}`} />
            <span className="text-[10px] mt-1 tracking-tight">Mais</span>
          </button>
        </div>
      </nav>

      {/* Gaveta / Drawer de Opções Extras no Mobile */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-50 block md:hidden">
          {/* Backdrop escurecido */}
          <div
            className="fixed inset-0 bg-gray-900/40 backdrop-blur-xs transition-opacity animate-in fade-in duration-200"
            onClick={closeMenu}
          />

          {/* Painel inferior deslizando para cima */}
          <div className="fixed bottom-0 left-0 right-0 max-h-[82vh] bg-white rounded-t-3xl shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom duration-200 border-t border-gray-100 pb-[max(env(safe-area-inset-bottom),1rem)]">
            {/* Header da Gaveta */}
            <div className="px-6 pt-5 pb-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/70">
              <div>
                <p className="text-xs font-semibold text-emerald-800 tracking-wider uppercase">Menu do Portal</p>
                <p className="text-sm font-bold text-gray-900 truncate max-w-[240px] mt-0.5">
                  {formatEmpresaDisplayName(empresa) || 'Minha Empresa'}
                </p>
              </div>
              <button
                type="button"
                onClick={closeMenu}
                className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200/60 rounded-full transition-colors"
                title="Fechar"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Lista de Links da Gaveta */}
            <div className="p-4 overflow-y-auto space-y-1 divide-y divide-gray-100">
              <div className="space-y-1 pb-3">
                {!isIntegracoes && (
                  <>
                    <Link
                      to="/compliance"
                      onClick={closeMenu}
                      className="flex items-center gap-3.5 px-3.5 py-2.5 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
                    >
                      <ShieldCheckIcon className="w-5 h-5 text-blue-600 shrink-0" />
                      <div>
                        <p className="text-gray-900 font-semibold text-sm">Compliance & Regulamentação</p>
                        <p className="text-xs text-gray-500 font-normal">PGR, PCMSO, Laudos SST e Procuração</p>
                      </div>
                    </Link>

                    <Link
                      to="/epis"
                      onClick={closeMenu}
                      className="flex items-center gap-3.5 px-3.5 py-2.5 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
                    >
                      <ClipboardDocumentCheckIcon className="w-5 h-5 text-emerald-600 shrink-0" />
                      <div>
                        <p className="text-gray-900 font-semibold text-sm">Gestão de EPIs</p>
                        <p className="text-xs text-gray-500 font-normal">Ficha de EPI e entregas biométricas NR-06</p>
                      </div>
                    </Link>

                    <Link
                      to="/resultados"
                      onClick={closeMenu}
                      className="flex items-center gap-3.5 px-3.5 py-2.5 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
                    >
                      <DocumentTextIcon className="w-5 h-5 text-indigo-600 shrink-0" />
                      <div>
                        <p className="text-gray-900 font-semibold text-sm">Resultados e Laudos</p>
                        <p className="text-xs text-gray-500 font-normal">Download de exames e ASOs emitidos</p>
                      </div>
                    </Link>

                    <Link
                      to="/faturamento"
                      onClick={closeMenu}
                      className="flex items-center gap-3.5 px-3.5 py-2.5 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
                    >
                      <CreditCardIcon className="w-5 h-5 text-teal-600 shrink-0" />
                      <div>
                        <p className="text-gray-900 font-semibold text-sm">Faturamento e Assinaturas</p>
                        <p className="text-xs text-gray-500 font-normal">Faturas, extrato e histórico de pagamentos</p>
                      </div>
                    </Link>
                    {hasModulo('gestao_cipa') && (
                      <Link
                        to="/cipa"
                        onClick={closeMenu}
                        className="flex items-center gap-3.5 px-3.5 py-2.5 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
                      >
                        <ShieldCheckIcon className="w-5 h-5 text-teal-600 shrink-0" />
                        <div>
                          <p className="text-gray-900 font-semibold text-sm">CIPA & Comissões (NR-5)</p>
                          <p className="text-xs text-gray-500 font-normal">Quadro de cipeiros, estabilidade CLT e atas</p>
                        </div>
                      </Link>
                    )}
                  </>
                )}

                {isIntegracoes && hasModulo('integracoes') && (
                  <Link
                    to="/integracoes"
                    onClick={closeMenu}
                    className="flex items-center gap-3.5 px-3.5 py-2.5 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
                  >
                    <KeyIcon className="w-5 h-5 text-amber-600 shrink-0" />
                    <div>
                      <p className="text-gray-900 font-semibold text-sm">Integrações & API</p>
                      <p className="text-xs text-gray-500 font-normal">Chaves de API, IPs autorizados e documentação</p>
                    </div>
                  </Link>
                )}
              </div>

              {/* Informações da Conta e Ações de Saída */}
              <div className="pt-3 space-y-1">
                <Link
                  to="/profile"
                  onClick={closeMenu}
                  className="flex items-center gap-3.5 px-3.5 py-2.5 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
                >
                  <UserCircleIcon className="w-5 h-5 text-gray-500 shrink-0" />
                  <div>
                    <p className="text-gray-900 font-semibold text-sm">{user?.name || 'Meu Perfil'}</p>
                    <p className="text-xs text-gray-500 font-normal">{user?.email || 'Alterar senha e dados'}</p>
                  </div>
                </Link>

                <button
                  type="button"
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3.5 px-3.5 py-2.5 rounded-xl text-sm font-medium text-rose-600 hover:bg-rose-50 active:bg-rose-100 transition-colors text-left"
                >
                  <ArrowRightOnRectangleIcon className="w-5 h-5 text-rose-600 shrink-0" />
                  <div>
                    <p className="font-semibold text-sm">Sair do Sistema</p>
                    <p className="text-xs text-rose-500/80 font-normal">Encerrar sessão no Portal do Cliente</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
